import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import useBoundStore from '../../store';
import { Virtuoso } from 'react-virtuoso';
import { BuyApartmentFlowPaths } from 'routes';
import { formatDealWord } from 'lib/utils';
import { toggleNotification, toggleTemporary } from 'api';
import { ApartmentDeal } from 'api/hooks/use-apartment-deals';
import useApartmentListSecondary from '../../api/hooks/use-apartment-list-secondary';
import Loader from 'components/loader';
import NotFound from 'components/not-found';
import IconButton from 'components/icon-button';
import ErrorMessage from 'components/error-message';
import { ApartmentNavigateProps } from 'screens/apartment';
import BaseScreen, { BASE_SCREEN_HEADER_HEIGHT } from 'components/base-screen';
import LoadMoreVirtualListFooter from 'components/load-more-virtual-list-footer';
import ApartmentListItem, { APARTMENT_LIST_ITEM_HEIGHT } from 'components/apartment-list-item';
import { ITEMS_PER_PAGE } from 'lib/constants';
import heart from 'assets/images/heart.svg';
import peset from 'assets/images/peset.svg';
import sort from 'assets/images/sort.svg';
import sortGreen from 'assets/images/sort-green.svg';
import bell from 'assets/images/bell.svg';
import bellFilled from 'assets/images/bell-filled.svg';
import temporaryYellow from '../../assets/images/temporary-yellow.svg';
import temporary from '../../assets/images/temporary.svg';
import styles from './styles.module.scss';
import AddFacilityToFavoriteListBottomSheet from 'components/favorite-list-item/modals/addFacilityToFavoriteListBottomSheet';
import { MarketTypes } from 'api/types';

const ApartmentListBuySecondary: React.FC = () => {
  const navigate = useNavigate();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<number>();
  const directionSort = useBoundStore((s) => s.directionSort);
  const listStyle = { height: `calc(100vh - ${BASE_SCREEN_HEADER_HEIGHT})` };

  const {
    data: apartments,
    total,
    isInitialLoading,
    isLoadingMore,
    setSize,
    error,
    mutate,
  } = useApartmentListSecondary();

  const [notificationArray, setNotificationArray] = useState<number[] | null>([]);
  const [temporaryArray, setTemporaryArray] = useState<number[] | null>([]);

  useEffect(() => {
    return () => {
      setSize(1);
    };
  }, []);

  const handleFavApartment = (apartment: ApartmentDeal) => async (e: React.MouseEvent) => {
    setSelectedFacilityId(apartment.id);
    setIsBottomSheetOpen(true);
    e.stopPropagation();
  };

  const handleNotificationToggle = (apartment: ApartmentDeal) => async (e: React.MouseEvent) => {
    if (notificationArray?.includes(apartment?.id)) {
      let filteredArray = notificationArray.filter((item) => item !== apartment?.id);
      setNotificationArray(() => filteredArray);
    } else {
      !apartment?.notificationEnabled &&
        setNotificationArray((prev) => prev && [...prev, apartment?.id]);
    }
    e.stopPropagation();
    await toggleNotification(apartment.id, mutate);
  };

  const handleTemporaryToggle = (apartment: ApartmentDeal) => async (e: React.MouseEvent) => {
    if (temporaryArray?.includes(apartment?.id)) {
      let filteredArray = temporaryArray.filter((item) => item !== apartment?.id);
      setTemporaryArray(() => filteredArray);
    } else {
      !apartment.isSavedInTemporaryFiles &&
        setTemporaryArray((prev) => prev && [...prev, apartment?.id]);
    }
    e.stopPropagation();
    await toggleTemporary(apartment, mutate);
  };

  return (
    <BaseScreen
      title={formatDealWord(total)}
      controls={
        <>
          <IconButton
            icon={directionSort ? sortGreen : sort}
            onClick={() => navigate(BuyApartmentFlowPaths.apartmentSortSecondary)}
            className={styles.icon}
            iconWidth={'30px'}
            iconHeight={'30px'}
          />
          <IconButton
            icon={peset}
            onClick={() => navigate(BuyApartmentFlowPaths.apartmentFiltersSecondary)}
          />
        </>
      }
      onBackButtonClick={() => navigate(-1)}
    >
      {error && apartments.length === 0 ? (
        <ErrorMessage className={styles.margined} message={error} />
      ) : isInitialLoading ? (
        <Loader className={styles.margined} />
      ) : apartments.length > 0 ? (
        <>
          <Virtuoso
            useWindowScroll
            context={{
              total,
              loading: isLoadingMore,
              per: ITEMS_PER_PAGE,
              loadedNum: apartments.length,
              loadMore: () => setSize((s) => s + 1),
            }}
            data={apartments}
            style={listStyle}
            className={styles.container}
            defaultItemHeight={APARTMENT_LIST_ITEM_HEIGHT}
            itemContent={(index, apartment) => (
              <ApartmentListItem
                {...apartment}
                isShowBuildingHouse
                onClick={() =>
                  navigate(BuyApartmentFlowPaths.apartmentSecondary, {
                    state: { id: apartment.id } as ApartmentNavigateProps,
                  })
                }
                icon={<IconButton onClick={handleFavApartment(apartment)} icon={heart} />}
                centreIcon={
                  <IconButton
                    onClick={handleTemporaryToggle(apartment)}
                    icon={
                      apartment.isSavedInTemporaryFiles || temporaryArray?.includes(apartment?.id)
                        ? temporaryYellow
                        : temporary
                    }
                  />
                }
                leftIcon={
                  <IconButton
                    onClick={handleNotificationToggle(apartment)}
                    icon={
                      apartment.notificationEnabled || notificationArray?.includes(apartment?.id)
                        ? bellFilled
                        : bell
                    }
                  />
                }
                className={clsx(index !== apartments.length - 1 && styles.border)}
                isHide={apartment?.hiddenUntil}
              />
            )}
            components={{ Footer: LoadMoreVirtualListFooter }}
          />
          {selectedFacilityId && (
            <AddFacilityToFavoriteListBottomSheet
              onDismiss={() => setIsBottomSheetOpen(false)}
              isOpen={isBottomSheetOpen}
              marketType={MarketTypes.SECONDARY}
              facilityIds={[selectedFacilityId]}
            />
          )}
        </>
      ) : (
        <NotFound />
      )}
    </BaseScreen>
  );
};

export default ApartmentListBuySecondary;
